<!-- Map.svelte

  When the overlay is closed we should stay at the same place where we opened
  it. Use <a> instead of <button> in Map overlay. Pressing button causes DOM
  redraw which scrolls us to the top of the page. We don't want that cause this
  app is shown in the Frame and we can not scroll frame.
-->
<script module>
  import { onMount, onDestroy } from 'svelte';
  import * as lib from '$lib/lib.js';
  import { geo, filterLocations } from '$lib/locations.js';
  import { _ } from 'svelte-i18n';
</script>

<script>
  import { validateRegion } from '$lib/region.js';

  const INITIAL_CENTER = [50.0755, 14.4378]; // Prague
  const INITIAL_ZOOM = 13;

  // FreeDraw simplifies polygons in current-zoom pixel space, so a region
  // created while the map is zoomed out gets its boundary permanently culled
  // (~100 m per pixel at zoom 10). Regions are therefore created at this
  // fixed high zoom, where the simplification tolerance is sub-meter, and
  // only then is the view moved to show them.
  const REGION_CREATE_ZOOM = 18;
  const DEFAULT_REGION_VALUE = '0';
  const CUSTOM_REGION_VALUE = String(lib.CUSTOM_REGION_VALUE);
  const POLYGON_OPTIONS = { className: 'leaflet-polygon', interactive: false };

  import { appState } from '$lib/state.svelte.js';

  let { filter, subscribeCallback, doneCallback, invalidCallback } = $props();

  // ---------------------------------------------------------------------
  // DOM refs (bound via bind:this instead of document.getElementById)
  // ---------------------------------------------------------------------
  let mapElement;
  let mapPreviewElement;
  let selectElement;

  // ---------------------------------------------------------------------
  // Leaflet handles. These wrap mutable third-party objects that Svelte
  // doesn't need to track for reactivity, so they stay outside $state.
  // ---------------------------------------------------------------------
  let L;              // leaflet module, set once in onMount (dynamic import)
  let FreeDraw;        // FreeDraw class, set once in onMount
  let FreeDrawModule;  // module namespace, needed for the polygon registry
  let FreeDrawPolygonModule;
  let map;             // full-size editable map
  let /** @type {any} */ mapPreview;       // small read-only preview map
  let freeDraw;         // FreeDraw layer attached to `map`
  let /** @type {any} */ currentPreviewPolygon; // polygon currently rendered on `mapPreview`
  let drawnPolygon;     // last polygon drawn via freeDraw (used for cleanup)
  let markers = [];     // freeDraw markers, cleared on disableDrawing

  // ---------------------------------------------------------------------
  // Component state - everything the UI needs to react to lives here.
  // ---------------------------------------------------------------------
  let state = $state({
    isOverlayVisible: false,
    isDrawing: false,
    wasModified: false,
    selectedRegion: DEFAULT_REGION_VALUE,
  });

  const modeButtonLabel = $derived(
    state.isDrawing
      ? $_('map.button_move', { default: 'Move' })
      : $_('map.button_draw', { default: 'Draw' })
  );

  // ---------------------------------------------------------------------
  // Polygon helpers
  // ---------------------------------------------------------------------
  function focusOnPolygon(polygon) {
    const bounds = polygon.getBounds();
    map.fitBounds(bounds);
    mapPreview.fitBounds(bounds);
  }

  function setMapPolygon(polygon) {
    freeDraw.create(polygon.getLatLngs()[0]);
  }

  function setPreviewPolygon(polygon) {
    if (currentPreviewPolygon) {
      currentPreviewPolygon.removeFrom(mapPreview);
    }
    currentPreviewPolygon = polygon.addTo(mapPreview);
  }

  function polygonFromLatLngs(latlngs) {
    return L.polygon(latlngs, POLYGON_OPTIONS);
  }

  // ---------------------------------------------------------------------
  // Global region state - a snapshot of the drawn region, taken when the
  // overlay session ends (doneMapCallback). Consumed by lib.subscribe(),
  // which only becomes pressable after that, so no other refresh is
  // needed. Returns false when the drawn region is invalid, the region
  // is dropped then instead of being stored and sent to the backend.
  // ---------------------------------------------------------------------
  function updateRegion() {
    if (!freeDraw) return true;
    const polygons = freeDraw.all();
    if (polygons.length > 0) {
      const geojson = polygons[0].toGeoJSON();
      if (!validateRegion(geojson.geometry).ok) {
        lib.haptic('notificationOccurred', 'error');
        freeDraw.clear();
        if (currentPreviewPolygon) {
          currentPreviewPolygon.removeFrom(mapPreview);
          currentPreviewPolygon = undefined;
        }
        state.selectedRegion = DEFAULT_REGION_VALUE;
        state.wasModified = false;
        appState.region = { modified: false, geojson: {} };
        return false;
      }
      appState.region = {
        modified: state.wasModified,
        geojson: geojson,
      };
    } else {
      appState.region = {
        modified: state.wasModified,
        geojson: {},
      };
    }
    return true;
  }

  // ---------------------------------------------------------------------
  // Drawing mode
  // ---------------------------------------------------------------------
  function setPreviewMapInteractive(enabled) {
    const toggle = (control) => (enabled ? control.enable() : control.disable());
    toggle(mapPreview.dragging);
    toggle(mapPreview.touchZoom);
    toggle(mapPreview.doubleClickZoom);
    toggle(mapPreview.scrollWheelZoom);
    toggle(mapPreview.boxZoom);
    toggle(mapPreview.keyboard);
    if (mapPreview.tap) toggle(mapPreview.tap);
  }

  function enableDrawing() {
    setPreviewMapInteractive(true);
    state.isDrawing = true;
    if (map.hasLayer(freeDraw)) {
      freeDraw.mode(FreeDraw.CREATE);
    }
  }

  function disableDrawing() {
    setPreviewMapInteractive(false);
    state.isDrawing = false;
    if (map.hasLayer(freeDraw)) {
      freeDraw.mode(FreeDraw.NONE);
    }

    // Remove markers
    markers.forEach((marker) => marker.remove());
    markers = [];

    // Remove any stray polygon reference
    if (drawnPolygon) {
      drawnPolygon.remove();
      drawnPolygon = null;
    }
  }

  function toggleDrawing() {
    if (state.isDrawing) {
      disableDrawing();
    } else {
      enableDrawing();
    }
  }

  // ---------------------------------------------------------------------
  // UI event handlers
  // ---------------------------------------------------------------------
  export function doneMapCallback() {
    disableDrawing();
    state.isOverlayVisible = false;

    const polygonDrawn = freeDraw.all()[0];
    const valid = updateRegion();

    if (valid && polygonDrawn !== undefined) {
      // FreeDraw already adds the polygon to `map`, so we just need to
      // mirror it onto the preview map.
      setPreviewPolygon(polygonFromLatLngs(polygonDrawn.getLatLngs()));
      focusOnPolygon(polygonDrawn);
    } else if (polygonDrawn === undefined) {
      // Reset to Prague if nothing was selected
      state.selectedRegion = DEFAULT_REGION_VALUE;
    }

    if (!valid && invalidCallback) {
      invalidCallback();
    }

    // Activate the subscribe button
    const text = $_('map.telegram_main_button_subscribe', { default: 'Subscribe' });
    lib.setTelegramButton(text, subscribeCallback, doneCallback);
  }

  function clearCallback() {
    lib.haptic('notificationOccurred', 'warning');
    state.wasModified = false;
    freeDraw.clear();
    if (currentPreviewPolygon) {
      currentPreviewPolygon.removeFrom(mapPreview);
      currentPreviewPolygon = undefined;
    }
  }

  function selectCallback() {
    const value = state.selectedRegion;
    const latlngs = geo[value].coordinates[0].map(
      (item) => new L.LatLng(item[1], item[0])
    );

    state.wasModified = false;
    freeDraw.clear();

    const polygonSelected = polygonFromLatLngs(latlngs);

    // Create the polygon at REGION_CREATE_ZOOM, then restore the view and
    // glide into the region. setZoom is synchronous, so nothing in between
    // is painted and the user only sees the flyToBounds animation.
    const prevZoom = map.getZoom();
    map.setZoom(REGION_CREATE_ZOOM, { animate: false });
    setMapPolygon(polygonSelected);
    setPreviewPolygon(polygonFromLatLngs(polygonSelected.getLatLngs()));
    map.setZoom(prevZoom, { animate: false });

    map.flyToBounds(latlngs, {
      animate: true,
      duration: 0.8,
    });
  }

  function drawCallback() {
    lib.haptic('selectionChanged');
    state.wasModified = true;
    state.selectedRegion = CUSTOM_REGION_VALUE;
    toggleDrawing();
  }

  function activateMap() {
    const text = $_('map.telegram_main_button_done', { default: 'Done' });
    lib.setTelegramButton(text, doneCallback, subscribeCallback);

    if (currentPreviewPolygon) {
      currentPreviewPolygon.removeFrom(mapPreview);
      currentPreviewPolygon = undefined;
    }

    state.isOverlayVisible = true;
    map.invalidateSize();
  }

  // ---------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------
  onMount(async () => {
    // leaflet and freedraw are dynamically imported as they need a
    // reference to `window`
    L = await import('leaflet');
    FreeDrawModule = await import('$lib/leaflet-freedraw/FreeDraw.js');
    FreeDrawPolygonModule = await import('$lib/leaflet-freedraw/helpers/Polygon.js');
    FreeDraw = FreeDrawModule.default;

    // Initialize maps
    const mapOptions = {
      doubleClickZoom: false,
      zoomControl: false,
      attributionControl: false,
    };
    const mapPreviewOptions = {
      doubleClickZoom: false,
      zoomControl: false,
      attributionControl: false,
    };

    map = L.map(mapElement, mapOptions).setView(INITIAL_CENTER, INITIAL_ZOOM);
    mapPreview = L.map(mapPreviewElement, mapPreviewOptions).setView(INITIAL_CENTER, INITIAL_ZOOM);
    setPreviewMapInteractive(false);
    mapPreviewElement.style.cursor = 'default';

    // Add tile layers
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
    }).addTo(map);

    L.control.attribution({ position: 'topright' }).addTo(map);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
    }).addTo(mapPreview);

    // Add free draw layer
    freeDraw = new FreeDraw({
      mode: FreeDraw.NONE,
      strokeWidth: 2,
      simplifyFactor: 2.1,
    });

    freeDraw.on('markers', () => {
      // We only allow the user to have a single region
      let index = 0;
      Array.from(FreeDrawModule.polygons.get(map).values()).forEach((polygon) => {
        if (index !== 0) {
          FreeDrawPolygonModule.removeFor(map, polygon);
        } else {
          index += 1;
        }
      });
    });

    map.addLayer(freeDraw);

    // By default, set the region to Prague. Create it at REGION_CREATE_ZOOM
    // for the same reason as in selectCallback, then fit the view to it.
    const latlngs = geo[0].coordinates[0].map((item) => [item[1], item[0]]);
    const polygonDefault = polygonFromLatLngs(latlngs);
    map.setZoom(REGION_CREATE_ZOOM, { animate: false });
    setMapPolygon(polygonDefault);
    setPreviewPolygon(polygonFromLatLngs(polygonDefault.getLatLngs()));
    focusOnPolygon(polygonDefault);
  });

  onDestroy(() => {
    if (map) map.remove();
    if (mapPreview) mapPreview.remove();
  });
</script>

<div id="map-overlay" style:visibility={state.isOverlayVisible ? 'visible' : 'hidden'}>
  <div bind:this={mapElement} id="map" class="map"></div>
  <div id="mycontrols">
    <select
      bind:this={selectElement}
      bind:value={state.selectedRegion}
      onchange={selectCallback}
      class="leafalet-mycontrol"
      id="map-location-select"
      size="1"
      name="location"
    >
      <option disabled value={DEFAULT_REGION_VALUE}>
        {$_('map.location_select_region', { default: 'Select region' })}
      </option>
      <option readonly value={CUSTOM_REGION_VALUE}>
        {$_('map.location_custom', { default: 'Custom' })}
      </option>
      <option readonly value="0">Praha</option>
      <optgroup label={$_('map.location_districts', { default: 'Districts' })}>
        {#each Object.keys(filterLocations.districts) as name}
          <option value={name}>
            {filterLocations.districts[name][0]}
          </option>
        {/each}
      </optgroup>
      <optgroup label={$_('map.location_city_parts', { default: 'City parts' })}>
        {#each Object.keys(filterLocations.city_parts) as name}
          <option value={name}>
            {filterLocations.city_parts[name][0]}
          </option>
        {/each}
      </optgroup>
      <optgroup label={$_('map.location_cadastral_communities', { default: 'Cadastral communities' })}>
        {#each Object.keys(filterLocations.cad_communities) as name}
          <option value={name}>
            {filterLocations.cad_communities[name][0]}
          </option>
        {/each}
      </optgroup>
    </select>

    <a
      class="leaflet-mycontrol"
      id="mode-button"
      onclick={drawCallback}
      onkeydown={(e) => e.key === 'Enter' && drawCallback()}
      onkeypress={(e) => e.key === ' ' && drawCallback()}
      role="button"
      tabindex="0"
    >
      {#if state.isDrawing}
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
          fill="none" stroke="currentColor" stroke-width="2"
          stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <polyline points="5 9 2 12 5 15" />
          <polyline points="9 5 12 2 15 5" />
          <polyline points="15 19 12 22 9 19" />
          <polyline points="19 9 22 12 19 15" />
          <line x1="2" y1="12" x2="22" y2="12" />
          <line x1="12" y1="2" x2="12" y2="22" />
        </svg>
      {:else}
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
          fill="none" stroke="currentColor" stroke-width="2"
          stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
        </svg>
      {/if}
      {modeButtonLabel}
    </a>

    <a
      class="leaflet-mycontrol"
      id="clear-button"
      onclick={clearCallback}
      onkeydown={(e) => e.key === 'Enter' && clearCallback()}
      onkeypress={(e) => e.key === ' ' && clearCallback()}
      role="button"
      tabindex="0"
    >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
        fill="none" stroke="currentColor" stroke-width="2"
        stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </svg>
      {$_('map.button_clear', { default: 'Clear' })}
    </a>
  </div>
</div>

<div id="map-preview">
  <button type="button" title="Open Map" onclick={activateMap} id="activate-map-btn">
    <div bind:this={mapPreviewElement} id="map-preview" class="map"></div>
  </button>
</div>
